import { Component, OnInit, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BankStore, ContactStore, InsuranceStore } from '../core/stores/base.store';
import { Insurance, InsuranceLoan, Rider } from '../core/models';
import { FuseConfirmationService } from '../core/services/confirmation/confirmation.service';
import { UtilityService } from '../core/services/utility.service';
import { LoanUtils, NotificationUtils } from '../core/utils';
import { parseYYYYMMDDToDate } from '../core/utils/date.utils';
import { InsurancePremiumPaymentDialogComponent } from './insurance-premium-payment-dialog/insurance-premium-payment-dialog.component';
import { InsuranceLoanPaymentDialogComponent } from './insurance-loan-payment-dialog/insurance-loan-payment-dialog.component';

@Component({
  selector: 'app-insurance',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatTableModule,
    MatMenuModule,
    MatSidenavModule,
    MatListModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  templateUrl: './insurance.component.html',
  styleUrls: ['./insurance.component.css']
})
export class InsuranceComponent {
  private insuranceStore = inject(InsuranceStore);
  private bankStore = inject(BankStore);
  private contactStore = inject(ContactStore);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  #fuseConfirmationService = inject(FuseConfirmationService);
  public utilityService = inject(UtilityService);
  // Computed signals
  dataSource = computed(() => this.insuranceStore.entities());
  loading = computed(() => this.insuranceStore.loading());
  selectedInsurance = computed(() => this.insuranceStore.selected());
  selectedLoan = computed(() => this.insuranceStore.selectedLoan());
  contacts = computed(() => this.contactStore.entities().filter(c => c.category === 'Insurance'));
  states = ['active', 'paid_off', 'pending', 'defaulted'];

  getLoansByState(state: string): any[] {
    return this.selectedInsurance()?.insurance_loans_collection?.filter(loan => loan.state === state) || [];
  }

  displayedColumns = ['name', 'bank', 'amount', 'balance', 'interestFee', 'closeDate', 'actions'];
  riderColumns = ['name', 'riderInsurance', 'riderPremium', 'cashValue', 'issueDate', 'actions'];

  constructor() {
    effect(() => {
      const insurances = this.dataSource();
      if (insurances.length > 0 && !this.selectedInsurance()) {
        this.selectInsurance(insurances[0]);
      }
    });
  }


  num_loans(loans: InsuranceLoan[] | undefined): string {
    if (!loans || loans.length === 0) {
      return '0 loans';
    }
    if (loans.length === 1) {
      return '1 loan';
    }
    return String(loans.length) + ' loans';
  }

  num_riders(riders: Rider[] | undefined): string {
    if (!riders || riders.length === 0) {
      return '0 riders';
    }
    if (riders.length === 1) {
      return '1 rider';
    }
    return String(riders.length) + ' riders';
  }
  
  /**
   * Calculate the next due date based on the paid to date
   * Assumes monthly premium payments
   */
  calculateNextDueDate(paidToDate: Date | string | null): Date | null {
    if (!paidToDate) {
      return null;
    }
    
    let date: Date;
    if (typeof paidToDate === 'string') {
      const parsedDate = parseYYYYMMDDToDate(paidToDate);
      if (!parsedDate) {
        return null;
      }
      date = new Date(parsedDate);
    } else {
      date = new Date(paidToDate);
    }
    
    // Add one month to the paid to date to get the next due date
    date.setMonth(date.getMonth() + 1);
    
    return date;
  }
  
  /**
   * Get the latest issue date from all riders
   * Returns the most recent date among all riders
   */
  getLatestRiderIssueDate(riders: Rider[] | undefined): Date | null {
    if (!riders || riders.length === 0) {
      return null;
    }
    
    let latestDate: Date | null = null;
    
    riders.forEach(rider => {
      if (rider.issue_date) {
        let riderDate: Date;
        
        if (typeof rider.issue_date === 'string') {
          const parsedDate = parseYYYYMMDDToDate(rider.issue_date);
          if (!parsedDate) {
            return;
          }
          riderDate = new Date(parsedDate);
        } else {
          riderDate = new Date(rider.issue_date);
        }
        
        if (!latestDate || riderDate > latestDate) {
          latestDate = riderDate;
        }
      }
    });
    
    return latestDate;
  }
  
  /**
   * Check if the policy is paid up to date
   * Returns true if both the base policy and all riders are paid up to date
   */
  isPolicyPaidUpToDate(): boolean {
    const insurance = this.selectedInsurance();
    if (!insurance) {
      return false;
    }
    
    // Check base policy paid_to_date
    let basePolicyPaidUp = false;
    if (insurance.paid_to_date) {
      let paidToDate: Date;
      if (typeof insurance.paid_to_date === 'string') {
        const parsedDate = parseYYYYMMDDToDate(insurance.paid_to_date);
        if (parsedDate) {
          paidToDate = new Date(parsedDate);
          // Set time to end of day to include today
          paidToDate.setHours(23, 59, 59, 999);
          // Compare with current date
          const today = new Date();
          basePolicyPaidUp = paidToDate >= today;
        }
      } else {
        paidToDate = new Date(insurance.paid_to_date);
        // Set time to end of day to include today
        paidToDate.setHours(23, 59, 59, 999);
        // Compare with current date
        const today = new Date();
        basePolicyPaidUp = paidToDate >= today;
      }
    }
    
    // Policy is paid up if both base policy and all riders are paid up
    return basePolicyPaidUp;
  }
  
  /**
   * Calculate the number of days until the next payment is due
   * Returns 0 if the policy is already due for payment
   */
  getDaysUntilDue(): number {
    const insurance = this.selectedInsurance();
    if (!insurance || !insurance.paid_to_date || !this.isPolicyPaidUpToDate()) {
      return 0;
    }
    
    let paidToDate: Date;
    if (typeof insurance.paid_to_date === 'string') {
      const parsedDate = parseYYYYMMDDToDate(insurance.paid_to_date);
      if (!parsedDate) {
        return 0;
      }
      paidToDate = new Date(parsedDate);
    } else {
      paidToDate = new Date(insurance.paid_to_date);
    }
    
    // Set time to end of day
    paidToDate.setHours(23, 59, 59, 999);
    
    // Calculate difference in days
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = paidToDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }

  /**
   * Opens the premium payment dialog for the selected insurance
   */
  openPremiumPaymentDialog(): void {
    if (!this.selectedInsurance()) {
      NotificationUtils.getInstance().showError('No insurance selected');
      return;
    }

    const dialogRef = this.dialog.open(InsurancePremiumPaymentDialogComponent, {
      width: '500px',
      data: {
        insurance: this.selectedInsurance(),
        banks: this.bankStore.entities()
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return; // Dialog was canceled
      }
      
      // Create a bank transaction if one was returned
      if (result.transaction) {
        this.bankStore.createChild({
          child: result.transaction,
          endpoint: 'bank_transaction',
          collectionName: 'bank_transactions_collection'
        });
      }
        
      // Update the insurance with the new paid_to_date if available
      if (result.paidToDate) {
        const updatedInsurance = this.selectedInsurance();
        updatedInsurance.paid_to_date = result.paidToDate;
        updatedInsurance.face_value = LoanUtils.parseFormattedNumber(updatedInsurance.face_value);
        updatedInsurance.premium = LoanUtils.parseFormattedNumber(updatedInsurance.premium);
        updatedInsurance.cash_value = LoanUtils.parseFormattedNumber(updatedInsurance.cash_value);
        delete updatedInsurance.riders_collection;
        delete updatedInsurance.insurance_loans_collection;

        this.insuranceStore.update(updatedInsurance);
      }
    });
  }

  /**
   * Opens the loan payment dialog for the selected insurance
   */
  openLoanPaymentDialog(): void {
    if (!this.selectedInsurance()) {
      NotificationUtils.getInstance().showError('No insurance selected');
      return;
    }

    if (!this.selectedInsurance().insurance_loans_collection || 
        this.selectedInsurance().insurance_loans_collection.length === 0) {
      NotificationUtils.getInstance().showError('No loans available for this insurance');
      return;
    }

    const dialogRef = this.dialog.open(InsuranceLoanPaymentDialogComponent, {
      width: '500px',
      data: {
        insurance: this.selectedInsurance(),
        banks: this.bankStore.entities()
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return; // Dialog was canceled
      }
      
      if (result.transaction) {
        this.bankStore.createChild({
          child: result.transaction,
          endpoint: 'bank_transaction',
          collectionName: 'bank_transactions_collection'
        });
      }
        
      if (result.loan) {
        this.insuranceStore.updateLoan({
          loan: result.loan,
          endpoint: 'insurance_loan',
          collectionName: 'insurance_loans_collection'
        });
      }
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.insuranceStore.setFilter({ search: filterValue });
  }

  applyRiderFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.insuranceStore.setFilter({ search: filterValue });
  }

  selectInsurance(insurance: Insurance) {
    this.insuranceStore.selectEntity(insurance);
  }

  addInsurance() {
    // Check if there are any insurance contacts first
    if (!this.hasInsuranceContacts()) {
      this.navigateToContactsWithNotification();
      return;
    }
    
    this.insuranceStore.selectEntity(null);
    this.router.navigate(['/insurance/add']);
  }

  editInsurance(insurance: Insurance) {
    this.router.navigate(['/insurance/edit', insurance.id]);
  }

  addInsuranceLoan() {
    this.router.navigate(['/insuranceloans/add']);
  }

  editInsuranceLoan(loan: InsuranceLoan) {
    this.insuranceStore.selectLoan(loan);
    this.router.navigate(['/insuranceloans/edit']);
  }
  
  /**
   * Check if there are any insurance contacts
   * Returns true if there are insurance contacts, false otherwise
   */
  hasInsuranceContacts(): boolean {
    return this.contacts().length > 0;
  }
  
  /**
   * Navigate to the contacts page with a query parameter to show a notification
   * about creating an insurance contact
   */
  navigateToContactsWithNotification() {
    // Use the static showError method from NotificationUtils
    NotificationUtils.getInstance().showError('You need to create an insurance contact before you can add insurance policies');
    this.router.navigate(['/contacts'], { 
      queryParams: { requireInsuranceContact: 'true' } 
    });
  }

  deleteInsuranceLoan(loan: InsuranceLoan) {
    const confirmation = this.#fuseConfirmationService.open({
      title: 'Delete Insurance Loan',
      message: 'Are you sure you want to delete this loan? This action cannot be undone!',
      icon: {
        show: true,
        name: 'warning',
        color: 'warn'
      },
      actions: {
        confirm: {
          label: 'Delete',
          color: 'warn'
        },
        cancel: {
          label: 'Cancel',
          show: true
        }
      }
    })

    confirmation.afterClosed().subscribe((result) => {
      // Handle both formats: string 'confirmed' (legacy) or object with result property (new)
      const isConfirmed = result === 'confirmed' || 
                         (result && typeof result === 'object' && result.result === 'confirmed');
                         
      if (isConfirmed) {
        this.insuranceStore.deleteLoan({loan:loan, endpoint: 'insurance_loan', collectionName: 'insurance_loans_collection'});
        this.router.navigate(['/insurance']);
      }
    })
  }

  getBank(id: number) {
    if (this.bankStore.entities().length != 0) {
      return this.bankStore.entities().find(b => b.id == id).name;
    }
    else {
      return '';
    }
  }

  addRider() {
    this.router.navigate(['/riders/add']);
  }

  getName(contactId: number): string {
    if (!contactId) return 'Unknown';
    
    const contact = this.contacts().find(c => c.id === contactId);
    if (!contact) return 'Unknown';
    
    return contact.organization + ' - ' + contact.first_name + ' ' + contact.last_name;
  }

  editRider(rider: any) {
    this.insuranceStore.selectChild(rider);
    this.router.navigate(['/riders/edit', rider.id]);
  }

  deleteRider(rider: Rider) {
    const confirmation = this.#fuseConfirmationService.open({
      title: 'Delete Rider',
      message: 'Are you sure you want to delete this rider? This action cannot be undone!',
      actions: {
        confirm: {
          label: 'Delete'
        }
      }
    })

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        this.insuranceStore.deleteChild({child:rider, endpoint: 'rider', collectionName: 'riders_collection'});
        this.router.navigate(['/insurance']);
      }
    })
  }

  // Computed values for insurance summary
  totalInsuranceAmount = computed(() => {
    const insurance = this.selectedInsurance();
    if (!insurance) return 0;
    
    const baseAmount = Number(insurance.face_value || 0);
    const riderAmount = this.calculateTotalRiderInsurance(insurance.riders_collection || []);
    
    return baseAmount + riderAmount;
  });

  totalPremiumAmount = computed(() => {
    const insurance = this.selectedInsurance();
    if (!insurance) return 0;
    
    const basePremium = Number(insurance.premium || 0);
    const riderPremium = this.calculateTotalRiderPremium(insurance.riders_collection || []);
    
    return basePremium + riderPremium;
  });

  totalCashValue = computed(() => {
    const insurance = this.selectedInsurance();
    if (!insurance) return 0;
    
    const baseCashValue = Number(insurance.cash_value || 0);
    const riderCashValue = this.calculateTotalRiderCashValue(insurance.riders_collection || []);
    
    return baseCashValue + riderCashValue;
  });

  /**
   * Calculate the total insurance amount from all riders
   */
  calculateTotalRiderInsurance(riders: Rider[]): number {
    return riders.reduce((total, rider) => total + Number(rider.face_value || 0), 0);
  }

  /**
   * Calculate the total premium amount from all riders
   */
  calculateTotalRiderPremium(riders: Rider[]): number {
    return riders.reduce((total, rider) => total + Number(rider.premium || 0), 0);
  }

  /**
   * Calculate the total cash value from all riders
   */
  calculateTotalRiderCashValue(riders: Rider[]): number {
    return riders.reduce((total, rider) => total + Number(rider.cash_value || 0), 0);
  }
}
